import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import { getOS } from 'utils/getOS';
//https://www.thisdot.co/blog/creating-custom-scrollbars-with-react

const CustomScrollbar = forwardRef((props, ref) => {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const scrollTrackRef = useRef(null);
  const scrollThumbRef = useRef(null);

  const scrollXTrackRef = useRef(null);
  const scrollXThumbRef = useRef(null);

  const observer = useRef(null);

  const [thumbHeight, setThumbHeight] = useState(0);
  const [scrollStartPosition, setScrollStartPosition] = useState(null);
  const [initialScrollTop, setInitialScrollTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const [thumbWidth, setThumbWidth] = useState(0);
  const [scrollXStartPosition, setScrollXStartPosition] = useState(null);
  const [initialScrollLeft, setInitialScrollLeft] = useState(0);

  const {
    maximalThumbYSize = 300,
    minimalThumbYSize = 100,
    maximalThumbXSize = 300,
    minimalThumbXSize = 100,
    onScrollStart,
    onScrollStop,
    onScroll,
    children,
    autoHeight,
    autoHeightMin,
    autoHeightMax,
    contentClass,
    ...restProps
  } = props;

  const isScrolling = useRef(false);
  const scrollFrame = useRef(null);

  const handleScroll = () => {
    if (!isScrolling.current) {
      isScrolling.current = true;
      onScrollStart?.();
    }

    if (scrollFrame.current) {
      cancelAnimationFrame(scrollFrame.current);
    }

    scrollFrame.current = requestAnimationFrame(() => {
      if (!!contentRef.current) {
        const currentScrollTop = contentRef.current.scrollTop;
        scrollFrame.current = requestAnimationFrame(() => {
          if (!!contentRef.current && currentScrollTop === contentRef.current.scrollTop) {
            isScrolling.current = false;
            onScrollStop?.();
          }
        });
      }
    });
  };
  const handleRef = useRef(null);
  useLayoutEffect(() => {
    if (contentRef.current) {
      handleRef.current = {
        container: containerRef.current,
        viewScrollTop: contentRef?.current?.scrollTop,
        view: contentRef.current,
        holderElement: contentRef.current,
        scrollToTop: () => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          }
        },
        scrollToBottom: () => {
          if (contentRef.current) {
            contentRef.current.scrollTop = contentRef.current.scrollHeight;
          }
        },
        scrollToLeft: () => {
          if (contentRef.current) {
            contentRef.current.scrollLeft = 0;
          }
        },
        getScrollTop: () => {
          if (contentRef.current) {
            return contentRef.current.scrollTop;
          }
        },
        getClientHeight: () => {
          if (contentRef.current) {
            return contentRef.current.clientHeight;
          }
        },
        getScrollHeight: () => contentRef.current?.scrollHeight,
        scrollTop: (value) => {
          if (contentRef.current) {
            contentRef.current.scrollTop = value ?? 0;
          }
        },
        scrollTo: (x, y) => {
          if (contentRef.current) {
            contentRef.current.scrollTo({ top: y ?? 0, left: x ?? 0, behavior: 'smooth' });
          }
        },
        getValues: () => {
          if (contentRef.current) {
            const { scrollTop, scrollLeft, clientHeight, clientWidth, scrollHeight, scrollWidth } = contentRef?.current;
            return { scrollTop, scrollLeft, clientHeight, clientWidth, scrollHeight, scrollWidth };
          }
        },
      };
    }
  }, [contentRef.current]);

  useImperativeHandle(ref, () => handleRef.current);

  function handleResize(ref, trackSizeY, trackSizeX) {
    const { clientHeight, scrollHeight, clientWidth, scrollWidth } = ref;
    const thumbSizeYCalculated = (clientHeight / scrollHeight) * trackSizeY;
    const thumbSizeXCalculated = (clientWidth / scrollWidth) * trackSizeX;
    const thumbYSize = Math.min(thumbSizeYCalculated, maximalThumbYSize);
    const thumbXSize = Math.min(thumbSizeXCalculated, maximalThumbXSize);

    setThumbHeight(clientHeight === scrollHeight ? 0 : Math.max(thumbYSize, minimalThumbYSize));
    setThumbWidth(clientWidth === scrollWidth ? 0 : Math.max(thumbXSize, minimalThumbXSize));
  }

  const handleTrackClick = useCallback(
    (e, type) => {
      e.preventDefault();
      e.stopPropagation();
      const { current: contentCurrent } = contentRef;
      const { clientY, clientX } = e;
      const target = e.target;
      const rect = target.getBoundingClientRect();

      if (type === 'x') {
        if (scrollXThumbRef.current && scrollXThumbRef.current?.contains(e.target)) {
          return; // Stop execution if clicking the thumb
        }
        const { current: trackXCurrent } = scrollXTrackRef;
        if (trackXCurrent && contentCurrent) {
          const trackLeft = rect.left;
          const thumbXOffset = -(thumbWidth / 2);
          const clickXRatio = (clientX - trackLeft + thumbXOffset) / trackXCurrent?.clientWidth;
          const scrollXAmount = Math.floor(clickXRatio * contentCurrent.scrollWidth);
          contentCurrent.scrollTo({
            left: scrollXAmount,
            behavior: 'smooth',
          });
        }
      } else {
        if (scrollThumbRef.current && scrollThumbRef.current.contains(e.target)) {
          return; // Stop execution if clicking the thumb
        }

        const { current: trackCurrent } = scrollTrackRef;
        if (trackCurrent && contentCurrent) {
          const trackTop = rect.top;
          const thumbYOffset = -(thumbHeight / 2);
          const clickYRatio = (clientY - trackTop + thumbYOffset) / trackCurrent?.clientHeight;
          const scrollYAmount = Math.floor(clickYRatio * contentCurrent.scrollHeight);

          contentCurrent.scrollTo({
            top: scrollYAmount,
            behavior: 'smooth',
          });
        }
      }
    },
    [thumbHeight, thumbWidth]
  );

  const handleThumbPosition = useCallback(
    (e) => {
      if (!contentRef.current || !scrollTrackRef.current || !scrollThumbRef.current) {
        return;
      }
      const {
        scrollTop: contentTop,
        scrollHeight: contentHeight,
        clientHeight: contentClientHeight,
      } = contentRef.current;
      const { clientHeight: trackHeight } = scrollTrackRef.current;

      const dynamicThumbHeight = Math.max(
        Math.min((trackHeight / contentHeight) * trackHeight, maximalThumbYSize),
        minimalThumbYSize
      );

      let newTop = (contentTop / (contentHeight - contentClientHeight)) * (trackHeight - dynamicThumbHeight);

      newTop = Math.min(newTop, trackHeight - thumbHeight);
      const thumb = scrollThumbRef.current;
      thumb.style.top = `${newTop}px`;

      // Horizontal scroll:

      if (!contentRef.current || !scrollXTrackRef.current || !scrollXThumbRef.current) {
        return;
      }
      const {
        scrollLeft: contentLeft,
        scrollWidth: contentWidth,
        clientWidth: contentClientWidth,
      } = contentRef.current;
      const { clientWidth: trackWidth } = scrollXTrackRef.current;

      const dynamicThumbWidth = Math.max(
        Math.min((trackWidth / contentWidth) * trackWidth, maximalThumbXSize),
        minimalThumbXSize
      );

      let newLeft = (contentLeft / (contentWidth - contentClientWidth)) * (trackWidth - dynamicThumbWidth);

      newLeft = Math.min(newLeft, trackWidth - thumbHeight);
      const thumbX = scrollXThumbRef.current;
      thumbX.style.left = `${newLeft}px`;
    },
    [children]
  );

  const handleThumbYMousedown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollStartPosition(e.clientY);
    if (contentRef.current) setInitialScrollTop(contentRef.current.scrollTop);
    setIsDragging(true);
  }, []);

  const handleThumbXMousedown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollXStartPosition(e.clientX);
    if (contentRef.current) setInitialScrollLeft(contentRef.current.scrollLeft);
    setIsDragging(true);
  }, []);

  const handleThumbMouseup = useCallback(
    (e) => {
      e.preventDefault();
      // Only stopPropagation if mouseup target is the vertical or horizontal thumb
      if (
        (scrollThumbRef.current && scrollThumbRef.current === e.target) ||
        (scrollXThumbRef.current && scrollXThumbRef.current === e.target)
      ) {
        e.stopPropagation();
      }
      if (contentRef.current) {
        contentRef.current.style.removeProperty('scroll-behavior');
      }
      if (isDragging) {
        setIsDragging(false);
      }
    },
    [isDragging]
  );

  const handleThumbMousemove = useCallback(
    (e) => {
      e.preventDefault();
      //OP: WE SHOULD DO e.stopPropagation() too, to do not impact other components with 'mousemove' events, but it causes issues with the scroll event when mouse is released outside the scrollbar.
      if (isDragging) {
        if (contentRef.current) {
          contentRef.current.style.scrollBehavior = 'unset';
        }
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = contentRef.current;

        const deltaY =
          ((e.clientY - scrollStartPosition) / (clientHeight - thumbHeight)) * (scrollHeight - clientHeight);
        const deltaX = ((e.clientX - scrollXStartPosition) / (clientWidth - thumbWidth)) * (scrollWidth - clientWidth);

        const newScrollTop = Math.min(Math.max(initialScrollTop + deltaY, 0), scrollHeight - clientHeight);
        const newScrollLeft = Math.min(Math.max(initialScrollLeft + deltaX, 0), scrollWidth - clientWidth);

        contentRef.current.scrollTop = newScrollTop;
        contentRef.current.scrollLeft = newScrollLeft;
      }
    },
    [isDragging, scrollStartPosition, thumbHeight, scrollXStartPosition, thumbWidth]
  );

  // If the content and the scrollbar track exist, use a ResizeObserver to adjust height of thumb and listen for scroll event to move the thumb
  useEffect(() => {
    if (contentRef.current && scrollTrackRef.current && scrollXTrackRef.current) {
      const ref = contentRef.current;
      const { clientHeight: trackSizeY } = scrollTrackRef.current;
      const { clientWidth: trackSizeX } = scrollXTrackRef.current ?? {};
      observer.current = new ResizeObserver(() => {
        handleResize(ref, trackSizeY, trackSizeX);
      });
      observer.current.observe(ref);
      ref.addEventListener('scroll', handleThumbPosition);
      return () => {
        observer.current?.unobserve(ref);
        ref.removeEventListener('scroll', handleThumbPosition);
      };
    }
  }, [contentRef.current, scrollTrackRef.current, scrollXTrackRef.current, autoHeight, children]);

  useEffect(() => {
    handleThumbPosition();
  }, [children]);

  // Listen for mouse events to handle scrolling by dragging the thumb
  useEffect(() => {
    document.addEventListener('mousemove', handleThumbMousemove);
    document.addEventListener('mouseup', handleThumbMouseup);
    document.addEventListener('mouseleave', handleThumbMouseup);
    return () => {
      document.removeEventListener('mousemove', handleThumbMousemove);
      document.removeEventListener('mouseup', handleThumbMouseup);
      document.removeEventListener('mouseleave', handleThumbMouseup);
    };
  }, [handleThumbMousemove, handleThumbMouseup]);

  const getStyle = () => {
    let style = { ...restProps.style };
    if (autoHeight) {
      style = {
        ...style,
        height: 'auto',
        maxHeight: autoHeightMax ?? '100%',
        minHeight: autoHeightMin ?? 0,
      };
    }
    return style;
  };

  return (
    <>
      <div
        {...restProps}
        style={getStyle()}
        className={`${props?.className ?? ''} custom-scrollbars__container ${getOS()?.toLowerCase()}`}
        ref={containerRef}
      >
        <div
          className={`custom-scrollbars__content ScrollbarsCustom-Content ${contentClass ?? ''}`.trim()}
          ref={contentRef}
          style={autoHeight ? { position: 'relative', maxHeight: autoHeightMax ?? '100%' } : null}
          onScroll={({ target }) => {
            handleScroll();
            if (onScroll) {
              onScroll(target);
            }
          }}
        >
          {children}
        </div>
        <div
          className={`${
            props?.trackYClassName ?? ''
          } custom-scrollbars__track ScrollbarsCustom-Track ScrollbarsCustom-TrackY`}
          ref={scrollTrackRef}
          onClick={handleTrackClick}
          style={{ cursor: isDragging && 'grabbing' }}
        >
          <div
            className='custom-scrollbars__thumb ScrollbarsCustom-Thumb ScrollbarsCustom-ThumbY'
            ref={scrollThumbRef}
            onMouseDown={handleThumbYMousedown}
            style={{
              height: `${thumbHeight}px`,
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          />
        </div>
        <div
          className='custom-scrollbars__track ScrollbarsCustom-Track ScrollbarsCustom-TrackX'
          ref={scrollXTrackRef}
          onClick={(e) => handleTrackClick(e, 'x')}
          style={{ cursor: isDragging && 'grabbing' }}
        >
          <div
            className='custom-scrollbars__thumb ScrollbarsCustom-Thumb ScrollbarsCustom-ThumbX'
            ref={scrollXThumbRef}
            onMouseDown={handleThumbXMousedown}
            style={{
              width: `${thumbWidth}px`,
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          />
        </div>
      </div>
    </>
  );
});

export default CustomScrollbar;
